import { useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { signOut } from "../features/authentication/authenticationSlice";
import { cfpBaseUrl } from "../network/urls";

const APP_TABS_KEY = "appTabs";
const TAB_ID_KEY = "sessionId";
const PENDING_LOGOUT_TAB_KEY = "eops:pendingLogoutTab";

const getAppTabs = () => {
  try {
    const tabs = JSON.parse(localStorage.getItem(APP_TABS_KEY) || "[]");
    return Array.isArray(tabs) ? tabs : [];
  } catch {
    return [];
  }
};

const setAppTabs = (tabs) => {
  localStorage.setItem(APP_TABS_KEY, JSON.stringify(tabs));
};

const registerTab = (tabId) => {
  const tabs = getAppTabs();
  if (!tabs.includes(tabId)) {
    tabs.push(tabId);
    setAppTabs(tabs);
  }
};

const unregisterTab = (tabId) => {
  const tabs = getAppTabs().filter((id) => id !== tabId);
  setAppTabs(tabs);
  return tabs;
};

const callLogoutAPIKeepalive = (token) => {
  if (!token) return;

  fetch(`${cfpBaseUrl}customerFaciliationPortal/logOut`, {
    method: "PUT",
    headers: { Authorization: token },
    keepalive: true,
  }).catch(() => {});
};

/**
 * Logs out and clears storage when the last app tab is closed.
 * Page refresh in the same tab keeps the session (sessionStorage survives reload).
 */
const useLogoutOnTabClose = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const tokenRef = useRef(token);
  const tabIdRef = useRef(null);

  tokenRef.current = token;

  useLayoutEffect(() => {
    const pendingTabId = localStorage.getItem(PENDING_LOGOUT_TAB_KEY);
    const existingTabId = sessionStorage.getItem(TAB_ID_KEY);

    if (existingTabId) {
      localStorage.removeItem(PENDING_LOGOUT_TAB_KEY);
      tabIdRef.current = existingTabId;
      registerTab(existingTabId);
      return;
    }

    const tabId = uuidv4();
    sessionStorage.setItem(TAB_ID_KEY, tabId);
    tabIdRef.current = tabId;
    registerTab(tabId);

    if (pendingTabId) {
      localStorage.removeItem(PENDING_LOGOUT_TAB_KEY);
      callLogoutAPIKeepalive(tokenRef.current);
      localStorage.clear();
      sessionStorage.setItem(TAB_ID_KEY, tabId);
      registerTab(tabId);
      dispatch(signOut());
    }
  }, [dispatch]);

  useEffect(() => {
    const handlePageHide = (event) => {
      if (event.persisted || !tabIdRef.current) return;

      const remainingTabs = unregisterTab(tabIdRef.current);

      if (remainingTabs.length === 0) {
        localStorage.setItem(PENDING_LOGOUT_TAB_KEY, tabIdRef.current);
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, []);
};

export default useLogoutOnTabClose;
